/**
 * Windows Desktop Viewer Component
 * Uses the NICE DCV Web SDK routed through the backend proxy to avoid
 * self-signed cert and cross-origin issues. The backend terminates TLS
 * to the DCV server, so no manual certificate acceptance is needed.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { message, Spin, Button, Space, Typography, Card, Modal } from "antd";
import {
  DesktopOutlined,
  ReloadOutlined,
  PoweroffOutlined,
  UserOutlined,
  KeyOutlined,
  FullscreenOutlined,
  ArrowLeftOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  AppstoreOutlined,
  SnippetsOutlined
} from "@ant-design/icons";
import dcv from "./../../lib/dcvjs-esm/dcv";
import useWindowSize from "../../hooks/useWindowSize";
import { Config } from "../../config";
import axiosInstance from "../../utils/axios";
import {
  fetchWindowsDesktop,
  fetchWindowsCredentials,
  setConnectionStatus,
  performWindowsAction
} from "./store/windowsSlice";
import "./WindowsDesktopViewer.scss";

/** Base64url-encode a string (for embedding JWT in URL path) */
function base64urlEncode(str) {
  return btoa(str)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

const { Title, Text, Paragraph } = Typography;

const MAX_POLL_DURATION_MS = 15 * 60 * 1000;

function WindowsDesktopViewer() {
  const { width, height } = useWindowSize();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { desktopId } = useParams();

  const pollIntervalRef = useRef(null);
  const connectionRef = useRef(null);
  const authRef = useRef(null);
  const dcvRetryTimerRef = useRef(null);
  const dcvRetryCountRef = useRef(0);
  const authFailCountRef = useRef(0);
  const MAX_DCV_RETRIES = 20;
  const DCV_RETRY_INTERVAL_MS = 10000;
  const MAX_AUTH_RETRIES = 3;
  const AUTH_RETRY_INTERVAL_MS = 30000;

  const [desktop, setDesktop] = useState(null);
  const [dcvCredentials, setDcvCredentials] = useState(null);
  // loading → waiting → ready → connecting → connected | error
  const [connectionState, setConnectionState] = useState("loading");
  const [errorMessage, setErrorMessage] = useState(null);
  const [pollStartTime] = useState(() => Date.now());
  const [actionBusy, setActionBusy] = useState(false);

  /* ───────── Action handlers ───────── */

  const handleStop = useCallback(() => {
    Modal.confirm({
      centered: true,
      title: "Stop Desktop",
      icon: <PauseCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to stop this desktop?</p>
          <p style={{ color: "#888", fontSize: 13 }}>
            The instance will be paused. Your data is preserved and you can start it again later.
          </p>
        </div>
      ),
      okText: "Stop Desktop",
      okType: "primary",
      cancelText: "Cancel",
      maskClosable: true,
      onOk: async () => {
        setActionBusy(true);
        try {
          if (connectionRef.current) {
            try { connectionRef.current.close(); } catch (_) {}
            connectionRef.current = null;
          }
          await dispatch(performWindowsAction({ id: desktopId, action: "stop" })).unwrap();
          message.success("Desktop is stopping...");
          navigate("/windows");
        } catch (error) {
          message.error(error?.message || "Failed to stop desktop");
          setActionBusy(false);
        }
      }
    });
  }, [desktopId, dispatch, navigate]);

  const handleTerminate = useCallback(() => {
    Modal.confirm({
      centered: true,
      title: "Terminate Desktop",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to permanently delete this desktop?</p>
          <p style={{ color: "#ff4d4f", fontSize: 13 }}>
            This action cannot be undone. The EC2 instance and all data will be destroyed.
          </p>
        </div>
      ),
      okText: "Terminate",
      okType: "danger",
      cancelText: "Cancel",
      maskClosable: true,
      onOk: async () => {
        setActionBusy(true);
        try {
          if (connectionRef.current) {
            try { connectionRef.current.close(); } catch (_) {}
            connectionRef.current = null;
          }
          await dispatch(performWindowsAction({ id: desktopId, action: "terminate" })).unwrap();
          message.success("Desktop is being terminated...");
          navigate("/windows");
        } catch (error) {
          message.error(error?.message || "Failed to terminate desktop");
          setActionBusy(false);
        }
      }
    });
  }, [desktopId, dispatch, navigate]);

  const handleStart = useCallback(async () => {
    setActionBusy(true);
    try {
      await dispatch(performWindowsAction({ id: desktopId, action: "start" })).unwrap();
      message.success("Desktop is starting...");
      // Reset to loading state and let polling pick up the new phase
      setConnectionState("loading");
      setActionBusy(false);
    } catch (error) {
      message.error(error?.message || "Failed to start desktop");
      setActionBusy(false);
    }
  }, [desktopId, dispatch]);

  /* ───────── Data fetching ───────── */

  const loadDesktop = useCallback(async () => {
    try {
      const result = await dispatch(fetchWindowsDesktop(desktopId)).unwrap();
      setDesktop(result);
      return result;
    } catch (error) {
      setErrorMessage(error?.message || String(error));
      setConnectionState("error");
      return null;
    }
  }, [desktopId, dispatch]);

  const loadCredentials = useCallback(async () => {
    try {
      const result = await dispatch(fetchWindowsCredentials(desktopId)).unwrap();
      setDcvCredentials(result.credentials);
      return result.credentials;
    } catch (error) {
      if (error && typeof error === "object") {
        if (error.transient && error.status === 409) return null;
        if (error.status === 503) {
          setErrorMessage(error.message || "Desktop provisioning failed");
          setConnectionState("error");
          return null;
        }
      }
      // Treat other credential failures as transient — poll/retry loop will try again
      console.warn("Credentials fetch failed, will retry:", error?.message || String(error));
      return null;
    }
  }, [desktopId, dispatch]);

  /* ───────── Poll for desktop readiness ───────── */

  useEffect(() => {
    const pollDesktop = async () => {
      const elapsed = Date.now() - pollStartTime;
      if (elapsed > MAX_POLL_DURATION_MS) {
        setConnectionState("error");
        setErrorMessage("Timeout waiting for desktop to become ready.");
        clearInterval(pollIntervalRef.current);
        return;
      }

      const result = await loadDesktop();
      if (!result) return;

      if (result.phase === "Running" && result.dcvConnectionUrl) {
        const creds = await loadCredentials();
        if (creds) {
          setConnectionState("ready");
          clearInterval(pollIntervalRef.current);
        } else {
          setConnectionState("waiting");
        }
      } else if (result.phase === "Stopped") {
        setConnectionState("stopped");
        clearInterval(pollIntervalRef.current);
      } else if (result.phase === "Failed") {
        setConnectionState("error");
        setErrorMessage(result.message || "Desktop provisioning failed");
        clearInterval(pollIntervalRef.current);
      } else {
        setConnectionState("waiting");
      }
    };

    pollDesktop();
    pollIntervalRef.current = setInterval(pollDesktop, 5000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [loadDesktop, loadCredentials, pollStartTime]);

  /* ───────── DCV SDK: authenticate + connect ───────── */

  /** Check if a DCV error is an auth/password failure */
  const isDcvAuthError = (msg) => {
    if (!msg) return false;
    const lower = msg.toLowerCase();
    return lower.includes("wrong username or password") || lower.includes("wrong password");
  };

  /** Check if a DCV error is transient (DNS not propagated, session not ready, etc.) */
  const isDcvTransientError = (msg) => {
    if (!msg) return false;
    const lower = msg.toLowerCase();
    return (
      lower.includes("no such host") ||
      lower.includes("no session") ||
      lower.includes("not authorised") ||
      lower.includes("failed to reach") ||
      lower.includes("connection refused") ||
      lower.includes("websocket") ||
      lower.includes("1006") ||
      lower.includes("1011") ||
      lower.includes("failed to communicate")
    );
  };

  /** Schedule a DCV auth retry — uses longer interval and lower limit to avoid account lockout */
  const scheduleDcvAuthRetry = useCallback(
    (reason) => {
      if (authFailCountRef.current >= MAX_AUTH_RETRIES) {
        console.error("DCV auth max retries reached, giving up to avoid account lockout");
        setConnectionState("error");
        setErrorMessage("Authentication failed after multiple attempts. The password may not have been applied yet. Please wait a moment and try reconnecting.");
        return;
      }
      authFailCountRef.current += 1;
      console.log(
        `DCV auth error, retrying in ${AUTH_RETRY_INTERVAL_MS / 1000}s (attempt ${authFailCountRef.current}/${MAX_AUTH_RETRIES}): ${reason}`
      );
      setConnectionState("waiting");
      dcvRetryTimerRef.current = setTimeout(async () => {
        try {
          const response = await axiosInstance.get("auth/refresh_token");
          const newToken = response.data.token;
          if (newToken) localStorage.setItem("gomydesk_token", newToken);
        } catch (err) {
          console.warn("Token refresh before retry failed:", err);
        }
        const creds = await loadCredentials();
        if (creds) {
          setConnectionState("ready");
        } else {
          scheduleDcvAuthRetry(reason);
        }
      }, AUTH_RETRY_INTERVAL_MS);
    },
    [MAX_AUTH_RETRIES, AUTH_RETRY_INTERVAL_MS, loadCredentials]
  );

  /** Schedule a DCV connection retry — refreshes token + re-fetches credentials before retrying */
  const scheduleDcvRetry = useCallback(
    (reason) => {
      if (dcvRetryCountRef.current >= MAX_DCV_RETRIES) {
        console.error("DCV max retries reached, giving up");
        setConnectionState("error");
        setErrorMessage(reason);
        return;
      }
      dcvRetryCountRef.current += 1;
      console.log(
        `DCV transient error, retrying in ${DCV_RETRY_INTERVAL_MS / 1000}s (attempt ${dcvRetryCountRef.current}/${MAX_DCV_RETRIES}): ${reason}`
      );
      setConnectionState("waiting");
      dcvRetryTimerRef.current = setTimeout(async () => {
        try {
          const response = await axiosInstance.get("auth/refresh_token");
          const newToken = response.data.token;
          if (newToken) localStorage.setItem("gomydesk_token", newToken);
        } catch (err) {
          console.warn("Token refresh before retry failed:", err);
        }
        const creds = await loadCredentials();
        if (creds) {
          setConnectionState("ready");
        } else {
          scheduleDcvRetry(reason);
        }
      }, DCV_RETRY_INTERVAL_MS);
    },
    [MAX_DCV_RETRIES, DCV_RETRY_INTERVAL_MS, loadCredentials]
  );

  const startDcvConnection = useCallback(() => {
    if (!desktop?.dcvConnectionUrl || !dcvCredentials) return;

    // Route DCV traffic through the backend proxy to avoid self-signed cert
    // and cross-origin issues. The proxy terminates TLS to the DCV server.
    const token = localStorage.getItem("gomydesk_token");
    if (!token) {
      setConnectionState("error");
      setErrorMessage("Authentication token not found. Please log in again.");
      return;
    }
    const encodedToken = base64urlEncode(token);
    const proxyUrl = `${Config.REACT_APP_API_URL}windows/${desktopId}/dcv/${encodedToken}`;

    const username = dcvCredentials.username;
    const password = dcvCredentials.password;

    setConnectionState("connecting");
    console.log("DCV SDK version:", dcv.version.versionStr);
    console.log("Authenticating via proxy:", proxyUrl);
    dcv.setLogLevel(dcv.LogLevel.INFO);

    authRef.current = dcv.authenticate(proxyUrl, {
      promptCredentials: (auth, challenge) => {
        const hasUser = challenge.requiredCredentials.some((c) => c.name === "username");
        const hasPass = challenge.requiredCredentials.some((c) => c.name === "password");
        if (hasUser && hasPass) {
          auth.sendCredentials({ username, password });
        } else {
          console.warn("Unexpected DCV challenge:", challenge);
          message.error("Unsupported authentication challenge");
          setConnectionState("error");
          setErrorMessage("Unsupported DCV authentication challenge");
        }
      },
      error: (_auth, error) => {
        console.error("DCV auth error:", error.message);
        if (isDcvAuthError(error.message)) {
          scheduleDcvAuthRetry(error.message);
        } else if (isDcvTransientError(error.message)) {
          scheduleDcvRetry(error.message);
        } else {
          setConnectionState("error");
          setErrorMessage("Authentication failed: " + error.message);
        }
      },
      success: (_auth, result) => {
        const { sessionId, authToken } = result[0];
        console.log("DCV auth success, session:", sessionId);
        dcvRetryCountRef.current = 0;
        authFailCountRef.current = 0; // reset on success

        dcv
          .connect({
            url: proxyUrl,
            sessionId,
            authToken,
            divId: "dcv-display",
            baseUrl: window.location.origin + "/dcvjs",
            callbacks: {
              firstFrame: () => {
                console.log("DCV first frame received");
                setConnectionState("connected");
                dispatch(setConnectionStatus("connected"));
              },
              disconnect: (reason) => {
                console.log("DCV disconnected:", reason?.message);
                message.info("Desktop disconnected" + (reason?.message ? ": " + reason.message : ""));
                connectionRef.current = null;
                setConnectionState("ready");
              }
            },
            configuration: {
              observers: {
                displayLayout: () => ({ info: {} }),
                displayResolution: () => {},
                clipboard: (clipboardData) => {
                  // Remote → browser: sync remote clipboard text to local
                  if (clipboardData?.data) {
                    navigator.clipboard.writeText(clipboardData.data).catch(() => {});
                  }
                },
                keyboard: () => {}
              }
            }
          })
          .then((conn) => {
            connectionRef.current = conn;
            // Let DCV SDK handle paste events natively (Ctrl+V in the desktop)
            conn.captureClipboardEvents(true, window);
          })
          .catch((error) => {
            console.error("DCV connect error:", error.message);
            if (isDcvTransientError(error.message)) {
              scheduleDcvRetry(error.message);
            } else {
              setConnectionState("error");
              setErrorMessage("Connection failed: " + error.message);
            }
          });
      }
    });
  }, [desktop, dcvCredentials, desktopId, dispatch, scheduleDcvRetry, scheduleDcvAuthRetry]);

  /* ───────── Auto-connect when ready ───────── */

  useEffect(() => {
    if (connectionState === "ready") {
      startDcvConnection();
    }
  }, [connectionState, startDcvConnection]);

  /* ───────── Resize handling ───────── */

  useEffect(() => {
    if (connectionState === "connected" && connectionRef.current) {
      connectionRef.current
        .requestResolution(width, height - 88)
        .catch((err) => console.warn("Resolution request failed:", err.message));
    }
  }, [width, height, connectionState]);

  /* ───────── Proactive token refresh (on mount + every 5 min) ───────── */

  useEffect(() => {
    const doRefresh = async () => {
      try {
        const response = await axiosInstance.get("auth/refresh_token");
        const newToken = response.data.token;
        if (newToken) localStorage.setItem("gomydesk_token", newToken);
      } catch (err) {
        console.warn("Proactive token refresh failed:", err);
      }
    };

    doRefresh(); // immediate refresh on mount
    const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    const refreshTimer = setInterval(doRefresh, TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshTimer);
  }, []);

  /* ───────── Cleanup on unmount ───────── */

  useEffect(() => {
    return () => {
      if (connectionRef.current) {
        try { connectionRef.current.close(); } catch (_) {}
        connectionRef.current = null;
      }
      if (dcvRetryTimerRef.current) {
        clearTimeout(dcvRetryTimerRef.current);
      }
    };
  }, []);

  /* ───────── Helpers ───────── */

  const handleManualPaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      const dt = new DataTransfer();
      dt.setData("text/plain", text);
      document.dispatchEvent(new ClipboardEvent("paste", { clipboardData: dt, bubbles: true }));
      message.success("Pasted to desktop");
    } catch {
      message.warning("Clipboard access denied by browser — try Ctrl+V while the desktop is focused");
    }
  }, []);

  const copyField = (value, label) => {
    navigator.clipboard.writeText(value);
    message.success(`${label} copied — press Ctrl+V inside the desktop to paste`);
  };

  const toggleFullscreen = () => {
    const container = document.querySelector(".windows-viewer-container.connected");
    if (!document.fullscreenElement) {
      container?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleReconnect = () => {
    if (connectionRef.current) {
      try { connectionRef.current.close(); } catch (_) {}
      connectionRef.current = null;
    }
    startDcvConnection();
  };

  /* ───────── Render: loading / waiting ───────── */

  if (connectionState === "loading" || connectionState === "waiting") {
    return (
      <div className="windows-viewer-container loading">
        <Card className="status-card">
          <Space direction="vertical" align="center" size="large">
            <DesktopOutlined style={{ fontSize: 64, color: "#1890ff" }} />
            <Title level={3}>Preparing Windows Desktop...</Title>
            <Spin size="large" />
            <Text type="secondary">Authenticating with DCV server</Text>
          </Space>
        </Card>
      </div>
    );
  }

  /* ───────── Render: stopped ───────── */

  if (connectionState === "stopped") {
    return (
      <div className="windows-viewer-container stopped">
        <Card className="status-card">
          <Space direction="vertical" align="center" size="large">
            <PauseCircleOutlined style={{ fontSize: 64, color: "#faad14" }} />
            <Title level={3}>Desktop is Stopped</Title>
            <Paragraph type="secondary">
              {desktop?.persistent !== false
                ? "This desktop is paused. Your data is preserved. Start it again to resume your session."
                : "This desktop is stopping and will be terminated."}
            </Paragraph>
            <Space>
              {desktop?.persistent !== false && (
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleStart}
                  loading={actionBusy}
                  size="large"
                >
                  Start Desktop
                </Button>
              )}
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleTerminate}
                disabled={actionBusy}
              >
                Terminate
              </Button>
              <Button
                icon={<AppstoreOutlined />}
                onClick={() => navigate("/windows")}
              >
                My Desktops
              </Button>
            </Space>
          </Space>
        </Card>
      </div>
    );
  }

  /* ───────── Render: error ───────── */

  if (connectionState === "error") {
    return (
      <div className="windows-viewer-container error">
        <Card className="status-card">
          <Space direction="vertical" align="center" size="large">
            <PoweroffOutlined style={{ fontSize: 64, color: "#ff4d4f" }} />
            <Title level={3}>Connection Failed</Title>
            <Paragraph type="danger">{errorMessage}</Paragraph>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                Retry
              </Button>
              <Button onClick={() => navigate("/")}>Back to Desktops</Button>
            </Space>
          </Space>
        </Card>
      </div>
    );
  }

  /* ───────── Render: ready / connecting ───────── */

  if (connectionState === "ready" || connectionState === "connecting") {
    return (
      <div className="windows-viewer-container connecting">
        <Card className="status-card">
          <Space direction="vertical" align="center" size="large">
            <DesktopOutlined style={{ fontSize: 64, color: "#1890ff" }} />
            <Title level={3}>Connecting to Desktop...</Title>
            <Spin size="large" />
            <Text type="secondary">Authenticating with DCV server</Text>
          </Space>
        </Card>
        {/* Hidden div for DCV SDK to mount into */}
        <div id="dcv-display" style={{ display: "none" }} />
      </div>
    );
  }

  /* ───────── Render: connected — DCV display + floating toolbar ───────── */

  return (
    <div className="windows-viewer-container connected" style={{ width: "100%", height: height - 85 }}>
      {/* Floating toolbar */}
      <div className="dcv-toolbar">
        <Space size="small">
          {dcvCredentials && (
            <>
              <Button
                size="small"
                icon={<UserOutlined />}
                onClick={() => copyField(dcvCredentials.username, "Username")}
              >
                {dcvCredentials.username}
              </Button>
              <Button
                size="small"
                icon={<KeyOutlined />}
                onClick={() => copyField(dcvCredentials.password, "Password")}
              >
                Copy Password
              </Button>
            </>
          )}
          <Button size="small" icon={<SnippetsOutlined />} onClick={handleManualPaste} title="Paste clipboard to desktop" />
          <Button size="small" icon={<FullscreenOutlined />} onClick={toggleFullscreen} title="Fullscreen" />
          <Button size="small" icon={<ReloadOutlined />} onClick={handleReconnect} title="Reconnect" />
          {desktop?.persistent !== false && (
            <Button
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={handleStop}
              disabled={actionBusy}
              title="Stop Desktop"
            />
          )}
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={handleTerminate}
            disabled={actionBusy}
            title="Terminate Desktop"
          />
          <Button
            size="small"
            icon={<AppstoreOutlined />}
            onClick={() => navigate("/windows")}
            title="My Desktops"
          />
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate("/")} title="Back" />
        </Space>
      </div>

      {/* DCV SDK renders into this div */}
      <div id="dcv-display" className="dcv-display" />
    </div>
  );
}

export default WindowsDesktopViewer;
